import { Vehicle, Order, CarVoucher } from '@esportal/portal-service';
import {
	TokenTextAppearance,
	TokenTextColor,
	createTagOverride,
	TextTag,
	List,
	Text,
} from '@volkswagen-onehub/components-core';
import { UserInteractionServiceV1 } from '@volkswagen-onehub/user-interaction-service';

export function checkOldCar(dateTimeStamp: string | null, years: number) {
	if (!dateTimeStamp) return false;

	const actualDate = new Date();
	actualDate.setFullYear(actualDate.getFullYear() - years);

	return new Date(dateTimeStamp) > actualDate;
}

export function actionRedirect(userInteractionService: UserInteractionServiceV1, action: string) {
	const [actionPath, paramString] = action.split('?');
	const handlerName = actionPath.replace('action:', '');
	const handler = userInteractionService.get(handlerName);

	let params: Record<string, string | string[]> = {};
	let paramsActionActivate = '';

	// Reset si no viene dealer en paramString → poner uno “neutral”
	if (!paramString || !paramString.includes('dealer=')) {
		params.dealer = 'none';
		paramsActionActivate = 'dealer=none';
	}

	if (paramString) {
		paramsActionActivate = paramString;
		const paramPairs = paramString.split('&');

		paramPairs.forEach((pair) => {
			const [key, value] = pair.split('=');
			params[key] = value;
		});
	}

	// @ts-ignore
	handler.onActivate(params, paramsActionActivate);
}

export function checkDateWithinDays(date: string | number, days: number): boolean {
	if (!date) return true;

	const targetDate = typeof date === 'string' ? new Date(date) : new Date(date);
	if (isNaN(targetDate.getTime())) return true;

	const currentDate = new Date();
	const diffInMilisenconds = currentDate.getTime() - targetDate.getTime();
	const diffInDays = Math.floor(diffInMilisenconds / (1000 * 60 * 60 * 24));

	return diffInDays <= days;
}

export function normalizeToTimestamp(dateInput: number | string | Date): number {
	if (typeof dateInput === 'string') {
		return new Date(dateInput).getTime();
	} else if (typeof dateInput === 'number') {
		return dateInput > 1000000000000 ? dateInput : dateInput * 1000;
	} else {
		return dateInput.getTime();
	}
}

export const strTitlelize = (str: string) => {
	if (!str) return '';
	return str
		.toLowerCase()
		.split(' ') // separar palabras para capitalizar
		.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
		.join(' ')
		.split('.') // separar sociedades para capitalizar (S.A., S.L.U.)
		.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
		.join('.');
};

export const isVehicle = (
	vehicle: Vehicle | Order | CarVoucher | null | undefined,
): vehicle is Vehicle | CarVoucher => {
	return !!vehicle && 'vgedRegistrationPlate' in vehicle;
};

export const createRichtextTextOverrides = (
	appearance: TokenTextAppearance,
	color: TokenTextColor,
): Record<string, ReturnType<typeof createTagOverride>> => ({
	p: createTagOverride(Text, { tag: TextTag.p, appearance, color }),
	span: createTagOverride(Text, { tag: TextTag.span, appearance, color }),
	ul: createTagOverride(List, { appearance, gutter: 'static100' }),
	ol: createTagOverride(List, { isOrdered: true, gutter: 'static100', appearance }),
	h1: createTagOverride(Text, { tag: TextTag.h1, appearance, color, bold: true }),
	h2: createTagOverride(Text, { tag: TextTag.h2, appearance, color, bold: true }),
	h3: createTagOverride(Text, { tag: TextTag.h3, appearance, color, bold: true }),
});

const IS_LIST_ITEM = /^[ \t]*(\d+[.)]|[*+-])[ \t]/;
const IS_HEADER = /^[ \t]*#{1,6}[ \t]+/;

// 1. Normaliza los escapes exactamente con las mismas reglas que hacía Java
const normalizeEscapedCharacters = (value: string) => {
	if (!value) return value;
	return value
		.replace(/\\n/g, '\n')
		.replace(/\\t/g, '\t')
		.replace(/\\r/g, '\r')
		.replace(/\\b/g, '\b')
		.replace(/\\f/g, '\f')
		.replace(/\\-/g, '&hyphen;')
		.replace(/\\\+/g, '&plus;')
		.replace(/\\\*/g, '&ast;')
		.replace(/\\\\/g, '\\');
};

export const normalizeMarkdownForRichtext = (markdown: string = '') => {
	if (!markdown) return '\n';

	const unescaped = normalizeEscapedCharacters(markdown);

	// 2. Limpia los espacios antes y después de los saltos de línea (Equivalente a cleanMarkdownLineBreaks de Java)
	const cleaned = unescaped.replace(/[ \t]*\r?\n[ \t]*/g, '\n');

	// 3. Procesamos los saltos de línea
	const normalized = cleaned.replace(/\n+/g, (breaks, offset, source) => {
		// Encontrar la línea anterior y siguiente de forma óptima
		const prevNewlineIdx = source.lastIndexOf('\n', offset - 1);
		const prev = source.slice(prevNewlineIdx + 1, offset);

		const nextNewlineIdx = source.indexOf('\n', offset + breaks.length);
		const next = source.slice(offset + breaks.length, nextNewlineIdx < 0 ? source.length : nextNewlineIdx);

		const isNextList = IS_LIST_ITEM.test(next);
		const isPrevList = IS_LIST_ITEM.test(prev);
		const isNextHeader = IS_HEADER.test(next);

		// Regla 1 (Java): Entre elementos de lista, se deja un salto simple sin barras
		if (isNextList && isPrevList) {
			return '\n';
		}

		// Regla 2: Si lo siguiente es un Header, devuelve espacio en blanco antes del header
		if (isNextHeader) {
			return breaks + `\n&nbsp;\n\n`;
		}

		// Regla 3:  Si el siguiente es lista, o la línea anterior termina en \,
		// se mantienen los saltos ORIGINALES tal cual
		if (isNextList || prev.trimEnd().endsWith('\\')) {
			return breaks;
		}

		// Regla 4: Salida de lista hacia texto normal (o final)
		if (isPrevList && !isNextList) {
			return next.trim() ? breaks + '\\\n' : breaks;
		}

		// Regla 5: // Por CADA salto de línea original (\n), se añade un '\\\n'
		// Ej: \n -> \\\n  |  \n\n -> \\\n\\\n
		return '\\\n'.repeat(breaks.length);
	});

	// 4. Mantenemos el \n inicial (que asumo lo requiere tu Richtext)
	return `\n${normalized.trimStart()}`;
};

// Limpia HTML y markdown
export const cleanText = (text: string): string => {
	const element = document.createElement('textarea');
	element.innerHTML = text;
	return element.value
		.replace(/<[^>]*>/g, '') // Quita HTML (p. ej. las etiquetas <a> creadas por processedText)
		.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') // Quita enlaces markdown [label](url) -> label
		.replace(/\*\*?([^*]+)\*\*?/g, '$1') // Quita markdown bold
		.replace(/__?([^_]+)__?/g, '$1') // Quita markdown italic
		.trim();
};
